import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
  EmptyStateComponent,
  IconChipComponent,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { ObfuscatePipe } from '@principle-theorem/ng-management-tools';
import {
  BasicDialogService,
  CustomTooltipComponent,
  InputSearchFilter,
  NgCommonModule,
  ObservableDataTable,
  PaginatedFirestoreTable,
  toSearchData,
  toSearchStream,
  TrackByFunctions,
  TruncateTooltipDirective,
  TypedFormControl,
} from '@principle-theorem/ng-shared';
import { ReportingPermissions } from '@principle-theorem/principle-core/features';
import {
  CUSTOM_REPORT_STATUS_COLOUR_MAP,
  CustomReportStatus,
  TemplateScope,
  type ICustomReport,
} from '@principle-theorem/principle-core/interfaces';
import {
  REPORT_BUILDER_DATA_SOURCES,
  ReportBuilderDataSourceInstance,
  resolveDataSource,
} from '@principle-theorem/reporting';
import {
  DAY_MONTH_YEAR_FORMAT,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { ReportEditorService } from '../../../report-builder/report-editor.service';
import { CustomReportScopesComponent } from '../custom-report-scopes/custom-report-scopes.component';
import { DataSourceDisplayComponent } from '../data-source-display/data-source-display.component';
import { PreSelectedFiltersComponent } from '../pre-selected-filters/pre-selected-filters.component';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_DISPLAY_COLUMNS = ['name', 'description', 'summary', 'actions'];

interface ICustomReportRow {
  customReport: WithRef<ICustomReport>;
  scopes: TemplateScope[];
  isDisabled: boolean;
}

@Component({
  selector: 'pr-custom-reports-list',
  templateUrl: './custom-reports-list.component.html',
  styleUrls: ['./custom-reports-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkOverlayOrigin,
    RouterModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInput,
    MatIcon,
    MatButtonModule,
    MatTableModule,
    MatPaginatorModule,
    MatTooltip,
    MatMenuModule,
    NgCommonModule,
    PreSelectedFiltersComponent,
    EmptyStateComponent,
    DataSourceDisplayComponent,
    CustomReportScopesComponent,
    IconChipComponent,
    TruncateTooltipDirective,
    CustomTooltipComponent,
    ObfuscatePipe,
  ],
})
export class CustomReportsListComponent {
  private _reportEditor = inject(ReportEditorService);
  private _basicDialog = inject(BasicDialogService);
  private _organisation = inject(OrganisationService);
  private _hasEditPermissions = toSignal(
    this._organisation.userHasPermission$(
      ReportingPermissions.ReportingCustomReportEdit
    ),
    { initialValue: false }
  );
  readonly dateFormat = DAY_MONTH_YEAR_FORMAT;
  trackByRef = TrackByFunctions.ref<ICustomReportRow>('customReport.ref');
  table = new PaginatedFirestoreTable<ICustomReportRow>(DEFAULT_PAGE_SIZE);
  searchCtrl = new TypedFormControl<string | undefined>('');
  search: InputSearchFilter<ICustomReportRow>;

  customReports = input.required<WithRef<ICustomReport>[]>();
  displayedColumns = input(DEFAULT_DISPLAY_COLUMNS);
  selectByScope = input<TemplateScope | undefined>(undefined);
  reportSelected = output<WithRef<ICustomReport>>();

  options = computed(() => this._getOptions());
  filteredDisplayedColumns = computed(() =>
    this._getFilteredDisplayedColumns()
  );

  @ViewChild(MatPaginator)
  set tablePaginator(paginator: MatPaginator) {
    this.table.dataTable.dataSource.paginator = paginator;
  }

  constructor() {
    this.search = new InputSearchFilter<ICustomReportRow>(
      toSearchData(this.options),
      toSearchStream(this.searchCtrl),
      ['customReport.name']
    );
    this.table.dataTable = new ObservableDataTable(this.search.results$);
    this.table.dataTable.pageSize = DEFAULT_PAGE_SIZE;
    this.table.dataTable.displayColumns = DEFAULT_DISPLAY_COLUMNS;

    effect(() => {
      this.table.dataTable.displayColumns = this.filteredDisplayedColumns();
    });
  }

  getStatusColour(customReport: WithRef<ICustomReport>): string {
    return CUSTOM_REPORT_STATUS_COLOUR_MAP[customReport.status];
  }

  isInDraft(customReport: WithRef<ICustomReport>): boolean {
    return customReport.status === CustomReportStatus.Draft;
  }

  async publishReport(customReport: WithRef<ICustomReport>): Promise<void> {
    const response = await this._reportEditor.openCustomReportDialog({
      customReport,
      isPublishing: true,
    });
    if (!response || !response.formData) {
      return;
    }
    await Firestore.patchDoc(customReport.ref, {
      ...response.formData,
      status: CustomReportStatus.Published,
    });
  }

  async revertToDraft(customReport: WithRef<ICustomReport>): Promise<void> {
    await Firestore.patchDoc(customReport.ref, {
      status: CustomReportStatus.Draft,
    });
  }

  async deleteReport(customReport: WithRef<ICustomReport>): Promise<void> {
    const confirmed = await this._basicDialog.confirm({
      title: 'Delete Custom Report',
      prompt: 'Are you sure you want to delete this report?',
      submitColor: 'warn',
      submitLabel: 'Delete',
    });
    if (confirmed) {
      await Firestore.deleteDoc(customReport.ref);
    }
  }

  getDataSource(
    customReport: WithRef<ICustomReport>
  ): ReportBuilderDataSourceInstance | undefined {
    return resolveDataSource(
      customReport.dataSource,
      REPORT_BUILDER_DATA_SOURCES
    );
  }

  private _getOptions(): ICustomReportRow[] {
    const selectByScope = this.selectByScope();

    const rows = this.customReports()
      .filter((customReport) =>
        this._hasEditPermissions()
          ? true
          : customReport.status !== CustomReportStatus.Draft
      )
      .map((customReport) => {
        const dataSource = resolveDataSource(
          customReport.dataSource,
          REPORT_BUILDER_DATA_SOURCES
        );
        const scopes = dataSource?.scopes ?? [];
        const isDisabled = selectByScope
          ? !scopes.includes(selectByScope)
          : false;
        return {
          customReport,
          scopes,
          isDisabled,
        };
      });

    return sortBy(rows, (row) => row.customReport.name);
  }

  private _getFilteredDisplayedColumns(): string[] {
    const columns = this.displayedColumns();
    return this._hasEditPermissions()
      ? columns
      : columns.filter((column) => column !== 'actions');
  }
}
